import { PlayIcon, VideoIcon } from 'lucide-react';

import { Pill, Skeleton } from '@/components/ui';
import { useModulesData } from '@/features/modules';
import videoPlayIcon from '@/features/sessions/assets/video-play.svg';
import { useRecordingDrawer } from '@/features/sessions/hooks';
import { getSessionLabel, isTruthyOrZero } from '@/lib/utils';

const SESSION_LABEL = getSessionLabel();

const PreviousSessionRecordingSkeleton = () => {
  return (
    <div className="w-full h-auto min-h-105 bg-purplish-blue-light-cardbg/60 rounded-4xl p-6 md:p-8 relative flex flex-col flex-1 overflow-hidden">
      <Skeleton className="h-6 w-48 mb-4 md:mb-6" />

      <div className="flex-1 flex flex-col gap-2 justify-center">
        <Skeleton className="h-4 w-40 mb-2" />

        <div className="bg-white/20 rounded-2xl p-6 flex flex-col animate-pulse">
          <Skeleton className="h-8 w-24 mb-6" />

          <div className="space-y-1">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <Skeleton className="h-6 w-20" shape="pill" />
        </div>
      </div>
    </div>
  );
};

const PreviousSessionRecordingError = () => {
  return (
    <div className="w-full h-auto min-h-105 bg-purplish-blue-light-cardbg/60 rounded-4xl p-6 md:p-8 relative flex flex-col flex-1 overflow-hidden">
      <h4 className="text-base md:text-lg font-medium text-magical-moonlight-d1 mb-4 md:mb-6">
        Previous {SESSION_LABEL.capitalize} Recording
      </h4>

      <div className="flex-1 flex flex-col gap-2 justify-center items-center">
        <div className="rounded-2xl p-8 flex flex-col items-center justify-center w-full">
          <p className="text-sm text-red-600 text-center">
            Unable to load previous {SESSION_LABEL.lowercase} recording.
          </p>
          <p className="text-xs text-red-500 text-center mt-2">
            Please try again later.
          </p>
        </div>
      </div>
    </div>
  );
};

const PreviousSessionRecordingEmpty = () => {
  return (
    <div className="w-full h-auto min-h-105 bg-hot-sun-yellow-light-cardbg border border-hot-sun-light rounded-4xl p-6 md:p-8 relative flex flex-col flex-1 overflow-hidden">
      <h4 className="text-base md:text-lg font-medium text-magical-moonlight-d1 mb-1 md:mb-2">
        My Recordings Will Be Visible Here
      </h4>

      <p className="text-xs md:text-sm text-magical-moonlight-d1 mb-4 md:mb-6">
        Click on the card to play the video
      </p>

      <div className="flex-1 flex flex-col">
        <div className="relative bg-hot-sun-light/20 rounded-3xl p-16 md:p-20 flex flex-col items-center justify-center h-full">
          <img src={videoPlayIcon} alt="Play icon" />
        </div>
      </div>
    </div>
  );
};

export default function PreviousSessionRecording() {
  const { lastSession, isLoading, isError } = useModulesData();
  const { open: openRecordingDrawer } = useRecordingDrawer();

  if (isLoading) {
    return <PreviousSessionRecordingSkeleton />;
  }

  if (isError) {
    return <PreviousSessionRecordingError />;
  }

  if (!lastSession) {
    return <PreviousSessionRecordingEmpty />;
  }

  const getSessionAttendedInfo = (): {
    variant: 'default' | 'success' | 'error';
    label: string;
  } | null => {
    if (!lastSession.attendance) {
      return null;
    }
    if (lastSession.attendance.status === 'ATTENDED') {
      return {
        variant: 'success',
        label: 'Attended',
      };
    }
    return {
      variant: 'error',
      label: 'Missed',
    };
  };

  return (
    <div className="w-full h-auto min-h-105 bg-purplish-blue-light-cardbg/60 rounded-4xl p-6 md:p-8 relative flex flex-col flex-1 overflow-hidden">
      <h4 className="text-base md:text-lg font-medium text-magical-moonlight-d1 mb-4 md:mb-6">
        Previous {SESSION_LABEL.capitalize} Recording
      </h4>

      <div className="flex-1 flex flex-col gap-2 justify-center">
        <div className="text-xs text-magical-moonlight-d1 mb-2">
          Click on the card to play the recording
        </div>

        <button
          className="bg-purplish-blue-dark/75 rounded-2xl p-6 flex flex-col text-white relative overflow-hidden group"
          onClick={() => openRecordingDrawer(lastSession.id, 'session')}
        >
          {/* Hover backdrop with play button */}
          <div className="absolute inset-0 bg-magical-moonlight-dark/40 backdrop-blur-[1px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 ease-out rounded-2xl">
            <PlayIcon className="h-12 w-12 text-white" />
          </div>

          <div className="flex items-center mb-6">
            {lastSession?.moduleDetails?.banner?.url ? (
              <img
                src={lastSession?.moduleDetails?.banner?.url ?? ''}
                alt="Module Banner"
                className="w-12 h-12"
              />
            ) : (
              <VideoIcon className="h-12 w-12 text-gray-400" />
            )}
          </div>

          <div className="space-y-1">
            <h3 className="text-xl font-bold text-white">{lastSession.name}</h3>
            {isTruthyOrZero(lastSession.number) ? (
              <p className="text-blue-chaos-light text-md font-bold">
                {SESSION_LABEL.capitalize} {lastSession.number}
              </p>
            ) : null}
          </div>
        </button>

        {getSessionAttendedInfo() ? (
          <div className="flex gap-2 mt-4">
            <Pill variant={getSessionAttendedInfo()?.variant} size="md">
              {getSessionAttendedInfo()?.label}
            </Pill>
          </div>
        ) : null}
      </div>
    </div>
  );
}
